import { useState, type FormEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useGenerateActivationKey } from "@/api/mutations/activationKeys";

// Aktivierungsschluessel generieren (optional TTL in Sekunden). Der Klartext-Schluessel
// kommt in der Antwort → der Aufrufer zeigt ihn im OneTimeTokenDialog.
export function GenerateActivationKeyDialog({
  open,
  onOpenChange,
  licenseId,
  onGenerated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  licenseId: string;
  onGenerated: (plaintext: string) => void;
}) {
  const [ttl, setTtl] = useState("");
  const gen = useGenerateActivationKey();

  function submit(e: FormEvent) {
    e.preventDefault();
    const ttlSec = ttl.trim() === "" ? undefined : Math.max(1, parseInt(ttl, 10) || 0);
    gen.mutate(
      { licenseId, ttlSec },
      {
        onSuccess: (data) => {
          setTtl("");
          onOpenChange(false);
          onGenerated(data.activationKey);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="generate-key-dialog">
        <DialogHeader>
          <DialogTitle>Aktivierungsschluessel generieren</DialogTitle>
          <DialogDescription>
            Der Klartext-Schluessel wird nur einmal angezeigt. Danach existiert nur noch der Hash.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="key-ttl">Gueltigkeit in Sekunden (optional)</Label>
            <Input id="key-ttl" type="number" min={1} value={ttl} onChange={(e) => setTtl(e.target.value)} placeholder="Server-Default" data-testid="key-ttl" />
          </div>
          {gen.isError && (
            <div className="text-sm text-danger" data-testid="generate-key-error">
              {gen.error.message}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={gen.isPending}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={gen.isPending} data-testid="generate-key-submit">
              {gen.isPending ? "…" : "Generieren"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
